import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useScrollLock } from '@hooks';

// Importar componentes de la nueva estructura
import { AdminLayout, AdminTabNavigation } from './Layout';
import { DashboardTab } from './Dashboard';
import { EquipmentTab } from './Equipment';
import { BookingsTab } from './Bookings';
import { CategoriesTab } from './Categories';
import { UsersTab } from './Users';

// Importar hooks personalizados
import { useEquipments, useCategories } from '@hooks';
import { Equipment } from '../../types/equipment';
import { equipmentService } from '../../services/equipmentService';
import { AdminTabType } from './types';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Estados principales
  const [activeTab, setActiveTab] = useState<AdminTabType['value']>('dashboard');

  // Estados para filtros de equipment
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Custom hooks
  const { equipments, loading: equipmentsLoading, refreshEquipments } = useEquipments();
  const { categories, addCategory, updateCategory, deleteCategory } = useCategories();

  //TODO: add user management
  // const { users } = useUsers();

  // Scroll lock para modales (solo si hay modales en este nivel)
  useScrollLock(false);

  // Verificación de autenticación y permisos
  const isAdmin = user?.userType === 'admin';

  if (!user) {
    navigate('/login');
    return null;
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h1 className="mb-2 text-2xl font-bold text-gray-800">Acceso Denegado</h1>
          <p className="text-gray-600">No tienes permiso para acceder al panel de administración.</p>
        </div>
      </div>
    );
  }

  // Estadísticas para el dashboard
  const stats = {
    totalEquipment: equipments.length,
    activeBookings: 15, // Placeholder
    totalRevenue: 12450, // Placeholder
    newUsers: 8 // Placeholder
  };

  // Handler para borrar equipo
  const handleDeleteEquipment = async (equipment: Equipment) => {
    if (window.confirm(`¿Seguro que quieres borrar el equipo "${equipment.name}"?`)) {
      await equipmentService.deleteEquipment(equipment.id);
      refreshEquipments();
    }
  };

  // Render del contenido según la pestaña activa
  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardTab stats={stats} />;
      
      case 'equipment':
        return (
          <EquipmentTab
            equipments={equipments}
            searchTerm={searchTerm}
            selectedCategory={selectedCategory}
            onSearchChange={setSearchTerm}
            onCategoryChange={setSelectedCategory}
            loading={equipmentsLoading}
            onRefresh={refreshEquipments}
            onDeleteEquipment={handleDeleteEquipment}
          />
        );
      
      case 'bookings':
        return <BookingsTab />;
      
      case 'categories':
        return (
          <CategoriesTab
            categories={categories}
            equipments={equipments}
            onAddCategory={addCategory}
            onEditCategory={updateCategory}
            onDeleteCategory={deleteCategory}
          />
        );
      
      case 'users':
        return <UsersTab users={[]} />;
      
      default:
        return <DashboardTab stats={stats} />;
    }
  };

  return (
    <AdminLayout>
      <AdminTabNavigation 
        activeTab={activeTab} 
        onTabChange={setActiveTab} 
      />
      
      {renderTabContent()}


    </AdminLayout>
  );
};

export default AdminPage;
